// State machine for the "what does the operator do next" rail.
// Reads project state from existing hooks and computes a single recommendation
// in agency voice. Used by NextStep component on every phase page and by the
// Dashboard.

import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useDrafts } from './drafts';
import { useRuns } from './pipeline';
import { useSetupOutputs } from './setup';
import { getSetupReadiness } from './setupState';
import { stripBoldMarkers } from './textUtils';
import type { Project } from './projects';

export type NextStepState =
  | 'loading'
  | 'no_brief'
  | 'brief_no_strategy'
  | 'strategy_running'
  | 'strategy_uncurated'
  | 'strategy_curated'
  | 'content_running'
  | 'draft_pending'
  | 'draft_approved'
  | 'posted_idle'
  | 'last_run_failed';

export interface NextStep {
  state: NextStepState;
  headline: string;
  helper?: string;
  ctaLabel?: string;
  ctaHref?: string;
  tone: 'cyan' | 'emerald' | 'amber';
}

const LOADING: NextStep = { state: 'loading', headline: '', tone: 'cyan' };

export function useNextStep(projectId: string | undefined): NextStep {
  const project = useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId],
    queryFn: () => api<{ project: Project }>(`/api/projects/${projectId}`).then((r) => r.project),
  });
  const setup = useSetupOutputs(projectId);
  const drafts = useDrafts(projectId, 'all');
  const runs = useRuns(projectId);

  if (
    !projectId ||
    project.isLoading ||
    setup.isLoading ||
    drafts.isLoading ||
    runs.isLoading ||
    !project.data
  ) {
    return LOADING;
  }

  const p = project.data;
  const briefLen = (p.brief ?? '').trim().length;
  const readiness = getSetupReadiness(setup.data);
  const allDrafts = drafts.data ?? [];
  const latestDraft = allDrafts[0]; // useDrafts returns newest-first
  const allRuns = runs.data?.runs ?? [];
  const latestRun = allRuns[0];
  const hasAnySetupOutputs =
    (setup.data?.audiences.length ?? 0) > 0 ||
    (setup.data?.voices.length ?? 0) > 0 ||
    (setup.data?.personas.length ?? 0) > 0 ||
    (setup.data?.themes.length ?? 0) > 0;
  const isStrategyRunning = latestRun?.pipeline === 'setup' && latestRun?.status === 'running';
  const isContentRunning = latestRun?.pipeline === 'runtime' && latestRun?.status === 'running';

  // Order matters — earliest-blocking state wins.

  if (briefLen < 30) {
    return {
      state: 'no_brief',
      headline: 'Start with the brief.',
      helper:
        "Your strategy team can't assemble until you've described the brand. Open the voice intake or paste a brief in Strategy.",
      ctaLabel: 'Open Strategy',
      ctaHref: `/projects/${projectId}/setup`,
      tone: 'cyan',
    };
  }

  if (isStrategyRunning) {
    return {
      state: 'strategy_running',
      headline: 'Your strategy team is working.',
      helper: 'Usually 20 to 40 seconds. Watch them assemble candidates from your brief.',
      tone: 'cyan',
    };
  }

  if (isContentRunning) {
    return {
      state: 'content_running',
      headline: 'Your content team is drafting.',
      helper: "Usually around 30 seconds. We'll surface the draft when they finish.",
      tone: 'cyan',
    };
  }

  if (!hasAnySetupOutputs) {
    return {
      state: 'brief_no_strategy',
      headline: 'Brief saved. Your strategy team is ready to assemble.',
      helper:
        "They'll prepare audiences, voices, personas, themes, and a brand kit from your brief. Around 20–40 seconds.",
      ctaLabel: 'Assemble strategy team',
      ctaHref: `/projects/${projectId}/setup`,
      tone: 'emerald',
    };
  }

  // A failure on the latest run takes precedence over draft states — the
  // operator should see and address the failure before being nudged toward
  // approving a stale draft.
  if (latestRun?.status === 'failed') {
    return {
      state: 'last_run_failed',
      headline: 'Your last run hit a snag.',
      helper: latestRun.error?.slice(0, 140) ?? 'Have a look at the pipeline and try again.',
      ctaLabel: 'Open the pipeline',
      ctaHref:
        latestRun.pipeline === 'setup'
          ? `/projects/${projectId}/setup`
          : `/projects/${projectId}/generator`,
      tone: 'amber',
    };
  }

  // Latest draft outranks curation prompts — once a draft exists, the
  // operator's mind is on reviewing/publishing, not re-curating.
  const approvedUnpostedCount = allDrafts.filter((d) => d.status === 'approved').length;
  if (latestDraft && latestDraft.status === 'pending') {
    const topic = latestDraft.topicTitle ? stripBoldMarkers(latestDraft.topicTitle) : null;
    const helperParts: string[] = [];
    if (topic) helperParts.push(`Topic: ${topic}.`);
    if (approvedUnpostedCount > 0) {
      helperParts.push(
        `Also: ${approvedUnpostedCount} approved draft${approvedUnpostedCount === 1 ? '' : 's'} still waiting to be marked posted.`,
      );
    }
    return {
      state: 'draft_pending',
      headline: 'Draft ready for your review.',
      helper: helperParts.length > 0 ? helperParts.join(' ') : undefined,
      ctaLabel: 'Review draft',
      ctaHref: `/projects/${projectId}/posts/${latestDraft.id}`,
      tone: 'emerald',
    };
  }

  if (latestDraft && latestDraft.status === 'approved') {
    return {
      state: 'draft_approved',
      headline: 'Draft approved. Where did you publish it?',
      helper: 'Mark posted with the published URL so your content team can learn from it.',
      ctaLabel: 'Mark posted',
      ctaHref: `/projects/${projectId}/posts/${latestDraft.id}`,
      tone: 'emerald',
    };
  }

  if (!readiness.ready) {
    return {
      state: 'strategy_uncurated',
      headline: 'Your strategy team has prepared candidates.',
      helper: `Open the 1:1 panels and pick who's active. Missing: ${readiness.missing.join(', ')}.`,
      ctaLabel: 'Curate the team',
      ctaHref: `/projects/${projectId}/setup`,
      tone: 'emerald',
    };
  }

  if (latestDraft && latestDraft.status === 'posted') {
    return {
      state: 'posted_idle',
      headline: 'Latest post published. Ready for the next one?',
      ctaLabel: 'Brief the content team',
      ctaHref: `/projects/${projectId}/generator`,
      tone: 'emerald',
    };
  }

  return {
    state: 'strategy_curated',
    headline: 'Strategy curated. Ready to brief the content team.',
    helper: 'One click and your content team drafts the next post.',
    ctaLabel: 'Brief the content team',
    ctaHref: `/projects/${projectId}/generator`,
    tone: 'emerald',
  };
}
